// String formatting and utility functions
// Pure utility functions - no HTTP calls needed, all client-side processing

// ============================================================================
// 🎨 STRING FORMATTING UTILITIES
// ============================================================================

// Helper function to convert UPPERCASE strings to Title Case
export const formatToTitleCase = (str: string): string => {
  if (!str) return str;
  
  // Prepositions and articles that should remain lowercase (except when first word)
  const lowercaseWords = ['da', 'de', 'do', 'dos', 'das', 'e', 'em', 'para', 'com', 'por', 'a', 'o', 'as', 'os'];
  
  return str
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      // Always capitalize first word
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      
      // Keep prepositions lowercase unless they're the first word
      if (lowercaseWords.includes(word)) {
        return word;
      }
      
      // Capitalize other words
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

// Format department names with special handling
export const formatDepartmentName = (department: string): string => {
  if (!department) return department;
  
  // Special cases for department names
  const specialCases: Record<string, string> = {
    'BI': 'BI', // Keep BI as is
    'UX': 'UX', // Keep UX as is
    'SEO': 'SEO', // Keep SEO as is
    'CRIAÇAO': 'Criação',
    'PRODUÇAO': 'Produção',
    'PRODUÇAO FINANCEIRA': 'Produção Financeira',
    'TECNOLOGIA': 'Tecnologia',
    'ATENDIMENTO': 'Atendimento',
    'COMERCIAL': 'Comercial',
    'CONTAS': 'Contas',
    'DIRETORIA': 'Diretoria',
    'FINANCEIRO': 'Financeiro',
    'PLANEJAMENTO': 'Planejamento'
  };
  
  return specialCases[department] || formatToTitleCase(department);
};

// Format role/position names
export const formatRoleName = (role: string): string => {
  if (!role) return role;
  
  // Special handling for specific role patterns
  let formatted = role.toLowerCase();
  
  // Handle common role abbreviations and titles
  formatted = formatted
    .replace(/\bsr\b/g, 'Sr.')
    .replace(/\bjr\b/g, 'Jr.')
    .replace(/\bpl\b/g, 'Pl.')
    .replace(/\bcoordernador\(a\)/g, 'Coordenador(a)')
    .replace(/\bdiretor\(a\)/g, 'Diretor(a)')
    .replace(/\bgerente/g, 'Gerente')
    .replace(/\banalista/g, 'Analista')
    .replace(/\bespecialista/g, 'Especialista')
    .replace(/\bassistente/g, 'Assistente')
    .replace(/\bcoordenador/g, 'Coordenador')
    .replace(/\bdiretor/g, 'Diretor')
    .replace(/\bplanner/g, 'Planner')
    .replace(/\bdesigner/g, 'Designer')
    .replace(/\bdesenvolvedor/g, 'Desenvolvedor');
  
  return formatToTitleCase(formatted);
};

// Format person names
export const formatPersonName = (name: string): string => {
  if (!name) return name;
  
  // Handle name particles that should be lowercase
  const nameParticles = ['da', 'de', 'do', 'dos', 'das', 'e'];
  
  return name
    .toLowerCase()
    .split(' ')
    .map((part, index) => {
      // Always capitalize first and last word
      if (index === 0 || index === name.split(' ').length - 1) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      }
      
      // Keep name particles lowercase
      if (nameParticles.includes(part)) {
        return part;
      }
      
      // Capitalize other parts
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
};

// Example usage of formatting functions:
// formatPersonName("USER REDACTED ONE-ALT") → "User Redacted One-Alt"
// formatDepartmentName("TECNOLOGIA") → "Tecnologia"
// formatRoleName("COORDENADOR(A) DE BI") → "Coordenador(a) de BI"
// formatToTitleCase("BANCO DO BRASIL") → "Banco do Brasil"

// ============================================================================
// 🔍 CASE-INSENSITIVE COMPARISON UTILITIES
// ============================================================================

// Helper function for case-insensitive string comparison
export const caseInsensitiveEquals = (str1: string, str2: string): boolean => {
  if (!str1 || !str2) return str1 === str2;
  return str1.toLowerCase().trim() === str2.toLowerCase().trim();
};

// Helper function for case-insensitive string contains
export const caseInsensitiveIncludes = (haystack: string, needle: string): boolean => {
  if (!haystack) return false;
  if (!needle) return true; // Empty search should match everything
  return haystack.toLowerCase().includes(needle.toLowerCase());
};


// Helper function for case-insensitive array includes
export const caseInsensitiveArrayIncludes = (array: string[], searchValue: string): boolean => {
  if (!array || !searchValue) return false;
  return array.some(item => caseInsensitiveEquals(item, searchValue));
};

// Helper function to find item in array case-insensitively
export const findCaseInsensitive = <T>(
  array: T[], 
  predicate: (item: T) => string, 
  searchValue: string
): T | undefined => {
  if (!array || !searchValue) return undefined;
  return array.find(item => caseInsensitiveEquals(predicate(item), searchValue));
};

// Format currency helper
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Math.abs(value));
};